import {FC, memo, useCallback} from 'react';
import {Box, Flex, Link, Span, Stack} from '@chakra-ui/react';
import {EPromptType, IDefaultPrompt, useT} from '@kcast-fe/core';
import {Control, Controller, useFieldArray} from 'react-hook-form';
import {Textarea} from 'ui-kit';
import {IPersonalizationForm} from 'core';

interface IProps {
  defaultPrompts: Array<IDefaultPrompt>;
  control: Control<IPersonalizationForm>;
}

const CHARACTER_LIMIT = 40000;

const PromptsFormFC: FC<IProps> = ({control, defaultPrompts}) => {
  const {fields} = useFieldArray({control, keyName: '_id', name: 'prompts'});

  const {t} = useT();

  const getDefaultPromptText = useCallback(
    (name: EPromptType) => {
      return defaultPrompts.find((p) => p.name === name)?.prompt ?? '';
    },
    [defaultPrompts]
  );

  // Capitalize first letter of every word
  const parsePromptName = (name: string) => {
    return name
      .split('_')
      .map((s) => s.replace(/\b\w/g, (c) => c.toUpperCase()))
      .join(' ');
  };

  return (
    <Stack gap={'24px'}>
      {fields.map((field, index) => (
        <Controller
          key={field._id}
          control={control}
          name={`prompts.${index}`}
          rules={{validate: (v) => v.prompt.length <= CHARACTER_LIMIT}}
          render={({field: {onChange, value}}) => (
            <Stack gap={'8px'}>
              <Flex p={'0 4px'} alignItems={'center'} justifyContent={'space-between'}>
                <Flex gap={'8px'} alignItems={'center'}>
                  <Box>{parsePromptName(value.name)}</Box>
                  {!value.custom && (
                    <Span fontSize={'13px'} color={'{colors.textTertiary}'}>
                      {t('labels.inherited')}
                    </Span>
                  )}
                </Flex>
                <Link
                  onClick={() =>
                    onChange({
                      ...value,
                      prompt: getDefaultPromptText(value.name),
                      custom: !value.custom
                    })
                  }
                >
                  {value.custom ? t('actions.reset') : t('actions.pin')}
                </Link>
              </Flex>
              <Box>
                <Textarea
                  rows={18}
                  size={'lg'}
                  value={value.prompt}
                  autoresize={false}
                  limit={CHARACTER_LIMIT}
                  placeholder={t('placeholders.enterPrompt')}
                  onChange={(text) => onChange({...value, prompt: text, custom: true})}
                />
              </Box>
            </Stack>
          )}
        />
      ))}
    </Stack>
  );
};

export const PromptsForm = memo(PromptsFormFC);
